import React from 'react';
import { CalendarIcon, TrophyIcon, UsersIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useSocial } from '../../contexts/SocialContext';
import type { ChallengeState } from '../../types/social';
const STATE: Record<ChallengeState, {
  label: string;
  tone: 'ok' | 'brand' | 'warn' | 'neutral';
}> = {
  open: {
    label: 'Registration open',
    tone: 'ok'
  },
  in_progress: {
    label: 'Running',
    tone: 'brand'
  },
  judging: {
    label: 'Being judged',
    tone: 'warn'
  },
  complete: {
    label: 'Complete',
    tone: 'neutral'
  }
};
function range(from: string, to: string): string {
  const format = (value: string) => new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short'
  });
  return `${format(from)} – ${format(to)}`;
}
export function ChallengesPanel() {
  const {
    challenges,
    toggleChallenge
  } = useSocial();
  return <div className="space-y-5" data-id="element-5452" data-mp="3anwl268997jd">
      <Panel title="How challenges work" data-id="element-5453" data-mp="3aoxxof4x6237">
        <p className="max-w-[76ch] text-body-sm text-ink" data-id="element-5454" data-mp="341d47eernyeh">
          Challenges record participation, not standing. There are no public rankings of individual
          students, and no challenge publishes who finished last. Where a competition has results,
          the organiser publishes them — Campuzon does not generate them.
        </p>
      </Panel>

      <ul className="grid gap-4 lg:grid-cols-2" data-id="element-5455" data-mp="2z05x7hkg738v">
        {challenges.map(challenge => {
        const state = STATE[challenge.state];
        return <li key={challenge.id} data-id="element-5456" data-mp="3rpwg03x0ytbb">
              <Panel className="h-full" bodyClassName="flex h-full flex-col p-5 sm:p-6" data-id="element-5457" data-mp="3rotphx3m6svx">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-5458" data-mp="3dtiumjpde7rk">
                  <div className="min-w-0" data-id="element-5459" data-mp="3ioojnre2k2jv">
                    <p className="text-body-lg font-semibold text-ink" data-id="element-5460" data-mp="22bq63h1owltn">{challenge.name}</p>
                    <p className="text-body-sm text-ink-muted" data-id="element-5461" data-mp="27b2vo9jy46zx">
                      {challenge.category} · organised by {challenge.organiser}
                    </p>
                  </div>
                  <Badge tone={state.tone} data-id="element-5462" data-mp="7w1by4d1iz71">{state.label}</Badge>
                </div>

                <p className="mt-2 max-w-[70ch] text-body-sm text-ink" data-id="element-5463" data-mp="7ydq06fogpet">{challenge.description}</p>

                <dl className="mt-3 grid gap-3 sm:grid-cols-3" data-id="element-5464" data-mp="32ycxgphuxjg">
                  <div data-id="element-5465" data-mp="3v8lqop5b7gc4">
                    <dt className="text-body-sm text-ink-muted" data-id="element-5466" data-mp="3v70terrm1jak">Dates</dt>
                    <dd className="flex items-center gap-1.5 text-body text-ink" data-id="element-5467" data-mp="3qblleggaahm2">
                      <CalendarIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-5468" data-mp="3l9w5oni49frn" />
                      {range(challenge.opensAt, challenge.closesAt)}
                    </dd>
                  </div>
                  <div data-id="element-5469" data-mp="3cbkh0r0t8s1u">
                    <dt className="text-body-sm text-ink-muted" data-id="element-5470" data-mp="3ccc6rl7q7vk7">Taking part</dt>
                    <dd className="cz-num flex items-center gap-1.5 text-body text-ink" data-id="element-5471" data-mp="1ejy58cbudcdh">
                      <UsersIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-5472" data-mp="1ak7g0d1yr4b2" />
                      {challenge.participants}
                    </dd>
                  </div>
                  <div data-id="element-5473" data-mp="2xekhqk1ypllb">
                    <dt className="text-body-sm text-ink-muted" data-id="element-5474" data-mp="2xdri031f7wco">Entry</dt>
                    <dd className="text-body text-ink" data-id="element-5475" data-mp="327dmv91cevxx">
                      {challenge.team ? 'Teams' : 'Individual'}
                    </dd>
                  </div>
                </dl>

                <p className="mt-3 text-body-sm text-ink" data-id="element-5476" data-mp="29if2e59qzhpg">
                  <span className="text-ink-muted" data-id="element-5477" data-mp="29jzwxuw2b0nb">To submit: </span>
                  {challenge.submissionRequired}
                </p>
                <p className="mt-1.5 flex items-start gap-2 text-body-sm text-ink-muted" data-id="element-5478" data-mp="2ii22mnqqqhgx">
                  <TrophyIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-5479" data-mp="2nhxyu0t754lx" />
                  {challenge.recognition}
                </p>

                <div className="mt-auto pt-4" data-id="element-5480" data-mp="3twyyaghfk4x9">
                  {challenge.state === 'open' || challenge.registered ? <Button variant={challenge.registered ? 'secondary' : 'primary'} size="sm" onClick={() => toggleChallenge(challenge.id)} data-id="element-5481" data-mp="2t0h531qnolm">
                      {challenge.registered ? 'Withdraw' : 'Register'}
                    </Button> : <p className="text-body-sm text-ink-muted" data-id="element-5482" data-mp="68210wvyq3be">
                      Registration has closed for this one.
                    </p>}
                </div>
              </Panel>
            </li>;
      })}
      </ul>
    </div>;
}